import { staleAssetErrorMessage } from './staleAssets'

type SentryFrameLike = {
  abs_path?: string
  filename?: string
}

type SentryExceptionLike = {
  value?: string
  stacktrace?: {
    frames?: SentryFrameLike[]
  }
}

type SentryBreadcrumbLike = {
  category?: string
  level?: string
  message?: string
  data?: Record<string, unknown>
}

export type SentryEventLike = {
  message?: string
  exception?: {
    values?: SentryExceptionLike[]
  }
  breadcrumbs?: SentryBreadcrumbLike[]
}

export type SentryHintLike = {
  originalException?: unknown
}

const INJECTED_WEBVIEW_BRIDGE_MESSAGES = [
  'Error invoking callWebView: Java bridge method invocation error',
  'Error invoking isAllowAutoFillOnDomain: Java object is gone',
  'swbrowser.inNightMode is not a function',
]

const ASSET_PATH_PATTERN = /\/assets\/[^?#\s]+\.(?:js|css)(?:[?#][^\s]*)?$/i

export function sentryExceptionMessages(event: SentryEventLike): string {
  return event.exception?.values
    ?.map((value) => value.value)
    .filter((value): value is string => Boolean(value))
    .join('\n') || ''
}

export function isInjectedWebViewBridgeError(error: unknown): boolean {
  const message = staleAssetErrorMessage(error)
  return INJECTED_WEBVIEW_BRIDGE_MESSAGES.some((knownMessage) => message.includes(knownMessage))
}

function isLoadFailedMessage(value: unknown): boolean {
  const message = staleAssetErrorMessage(value).trim()
  return message === 'Load failed' || message === 'TypeError: Load failed'
}

function isAssetUrl(value: unknown): boolean {
  if (typeof value !== 'string' || value.length === 0) return false

  try {
    const url = new URL(value, 'https://app.virtualtoureasy.com')
    return ASSET_PATH_PATTERN.test(url.pathname)
  } catch {
    return ASSET_PATH_PATTERN.test(value)
  }
}

function hasFailedAssetFetchBreadcrumb(event: SentryEventLike): boolean {
  return event.breadcrumbs?.some((breadcrumb) => {
    if (breadcrumb.category !== 'fetch' || breadcrumb.level !== 'error') return false

    const breadcrumbUrl = breadcrumb.data?.url
    const breadcrumbMessage = breadcrumb.message

    return isAssetUrl(breadcrumbUrl) || isAssetUrl(breadcrumbMessage)
  }) || false
}

function hasAssetStackFrame(event: SentryEventLike): boolean {
  return event.exception?.values?.some((exception) => (
    exception.stacktrace?.frames?.some((frame) => (
      isAssetUrl(frame.filename) || isAssetUrl(frame.abs_path)
    )) || false
  )) || false
}

export function isAssetPreloadLoadFailedEvent(
  event: SentryEventLike,
  hint?: SentryHintLike,
): boolean {
  const hasLoadFailedMessage = [
    hint?.originalException,
    event.message,
    sentryExceptionMessages(event),
  ].some(isLoadFailedMessage)

  return hasLoadFailedMessage && (hasFailedAssetFetchBreadcrumb(event) || hasAssetStackFrame(event))
}

const UNDEFINED_VARIABLE_PATTERN = /Can't find variable: |is not defined/

function isUrlLessFrame(frame: SentryFrameLike): boolean {
  const location = frame.abs_path || frame.filename
  return !location || location === 'undefined' || location === '<anonymous>'
}

// Safari extensions and other injected scripts run without a script URL, so
// their ReferenceErrors (e.g. "Can't find variable: EmptyRanges") arrive with
// frames like `undefined:1701:541`. Our bundles always load from /assets/*.js.
export function isInjectedScriptReferenceError(event: SentryEventLike): boolean {
  return event.exception?.values?.some((exception) => {
    const frames = exception.stacktrace?.frames
    return Boolean(
      exception.value &&
      UNDEFINED_VARIABLE_PATTERN.test(exception.value) &&
      frames?.length &&
      frames.every(isUrlLessFrame),
    )
  }) || false
}
